/**
 * Cookie consent state — single source of truth for whether the visitor has
 * accepted, declined, or not yet answered.
 *
 * Storage: localStorage key `wrc_cookie_consent` with value 'accepted' |
 * 'declined' | absent. Persists across sessions on the same browser.
 *
 * Events: dispatches `wrc:consent-change` (detail.value = 'accepted' |
 * 'declined') on set, and `wrc:open-cookie-banner` when the user clicks the
 * footer "Cookie Preferences" link to re-open the banner.
 */

const STORAGE_KEY = 'wrc_cookie_consent';

export const CONSENT_ACCEPTED = 'accepted';
export const CONSENT_DECLINED = 'declined';

const CONSENT_CHANGE_EVENT = 'wrc:consent-change';
const OPEN_BANNER_EVENT = 'wrc:open-cookie-banner';

export function getConsent() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (_e) {
    return null;
  }
}

export function setConsent(value) {
  if (value !== CONSENT_ACCEPTED && value !== CONSENT_DECLINED) {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch (_e) {
    // Storage blocked / private mode — still dispatch the event so the
    // current session can apply the choice, just no persistence.
  }
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: { value } }));
}

export function clearConsent() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (_e) {
    /* ignore */
  }
}

export function onConsentChange(handler) {
  const wrapped = (e) => handler(e.detail.value);
  window.addEventListener(CONSENT_CHANGE_EVENT, wrapped);
  return () => window.removeEventListener(CONSENT_CHANGE_EVENT, wrapped);
}

export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent(OPEN_BANNER_EVENT));
}

export function onOpenPreferencesRequest(handler) {
  window.addEventListener(OPEN_BANNER_EVENT, handler);
  return () => window.removeEventListener(OPEN_BANNER_EVENT, handler);
}
